import ReactDOM from 'react-dom';
import style from './SignModal.module.scss';
import { MultiversXConnectView } from './components/MultiversXConnectView/MultiversXConnectView';
import { useCallback, useEffect, useState } from 'react';
import { LinkToAppView } from './components/LinkToAppView/LinkToAppView';
import { useGetSignMessageSession } from '@multiversx/sdk-dapp/hooks/signMessage/useGetSignMessageSession';
import useApi from 'src/hooks/useApi';
import { BscConnectView } from './components/BscConnectView/BscConnectView';
import { useSignMessage as useSignMessageWagmi } from 'wagmi';
import { useSignMessage as useSignMessageMultiversX } from '@multiversx/sdk-dapp/hooks/signMessage/useSignMessage';
import { SuccessfullyLinked } from './components/SuccessfullyLinked/SuccessfullyLinked';

export default function SignModal({ onClose, currentProvider }) {
    const { isLinked, loading: isApiLoading, error, checkAddressLink, submitSignature } = useApi();
    const { sessionId, signMessage: signMessageMultiversX, onAbort } = useSignMessageMultiversX();
    const {
        signMessage: signMessageWagmi,
        isLoading: isBscLoading,
        isSuccess: isBscSuccess,
        data: bscData,
        reset: resetBsc,
    } = useSignMessageWagmi();
    const messageSession = useGetSignMessageSession(sessionId);
    const [address, setAddress] = useState(null);
    const [code, setCode] = useState('');
    const [addressChecked, setAddressChecked] = useState(false); // Nouvelle variable de contrôle

    const isConnected = address !== null;
    const isLoading = isBscLoading || isApiLoading;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const signMessage = useCallback(async () => {
        if (!code.trim()) return null;

        if (currentProvider === 'BSC') {
            if (bscData) {
                return bscData;
            }

            await signMessageWagmi({ message: code });
            return null;
        }

        if (currentProvider === 'MULTIVERSX') {
            if (messageSession) onAbort();

            const mvxSignedMessage = await signMessageMultiversX({
                message: code,
                callbackRoute: '',
            });

            return mvxSignedMessage?.signature?.toString('hex');
        }

        return null;
    }, [code, bscData, currentProvider, messageSession, onAbort, signMessageMultiversX, signMessageWagmi]);

    const handleSubmit = useCallback(async () => {
        if (!code.trim()) return;

        const signedMessage = await signMessage();

        if (signedMessage) {
            resetBsc();
            setCode('');

            const success = await submitSignature(code, address, signedMessage);
            if (success) {
                await checkAddressLink(address);
            }
        }
    }, [address, checkAddressLink, code, signMessage, submitSignature, resetBsc]);

    useEffect(() => {
        if (isBscSuccess) {
            handleSubmit();
        }
    }, [isBscSuccess, bscData, handleSubmit]);

    return ReactDOM.createPortal(
        <div className={style.overlay} onClick={handleOverlayClick}>
            <div className={style.signModal}>
                {!isConnected ? (
                    <>
                        {currentProvider === 'MULTIVERSX' && <MultiversXConnectView setAddress={setAddress} />}
                        {currentProvider === 'BSC' && <BscConnectView setAddress={setAddress} />}
                    </>
                ) : (
                    <>
                        {isLinked ? (
                            <SuccessfullyLinked onClose={onClose} />
                        ) : (
                            <LinkToAppView
                                setCode={setCode}
                                submitSignature={handleSubmit}
                                loading={isLoading}
                                code={code}
                                error={error}
                            />
                        )}
                    </>
                )}
            </div>
        </div>,
        document.body,
    );
}
